import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './button';
import { Activity as ActivityIcon, Maximize2, X, ImageIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const API_HOST = process.env.REACT_APP_API_URL || '';

const WebhookMapsView = ({ lastSeenAt = '' }) => {
  const [maps, setMaps] = useState(null);
  const [error, setError] = useState(null);
  const [enlarged, setEnlarged] = useState(null);

  const fetchMaps = useCallback(async () => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/webhook_maps`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch webhook maps');
      const data = await response.json();
      const list = Array.isArray(data?.maps) ? data.maps : [];
      const withHost = list.map((m) =>
        typeof m?.imageUrl === 'string' && m.imageUrl.startsWith('/api/')
          ? { ...m, imageUrl: `${API_HOST}${m.imageUrl}` }
          : m,
      );
      setMaps(withHost);
    } catch (err) {
      setError(err.message || 'Failed to fetch webhook maps');
      setMaps([]);
    }
  }, []);

  useEffect(() => {
    fetchMaps();
  }, [fetchMaps]);

  if (maps === null) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-gray-500">
        Loading webhook maps…
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-red-500">
        {error}
      </div>
    );
  }

  if (maps.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-500 p-6 text-center">
        <ActivityIcon className="h-8 w-8 mb-2" />
        <p className="text-sm">No webhook maps yet.</p>
        <p className="text-xs text-gray-400 mt-1">
          Auto-generated maps from your Strava activities will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-2 max-h-[60vh] lg:max-h-[72vh]">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 lg:gap-4">
          {maps.map((m) => {
            const isNew = !!(lastSeenAt && m.created_at && m.created_at > lastSeenAt);
            return (
              <div
                key={m.id}
                className="relative group rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm"
              >
                <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center">
                  {m.imageUrl ? (
                    <img
                      src={m.imageUrl}
                      alt={m.activity_name || 'Activity map'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-gray-400" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setEnlarged(m)}
                  className="absolute top-2 right-2 bg-black/55 hover:bg-black/75 text-white rounded-full p-1.5 lg:p-2 shadow-md transition-colors"
                  title="View larger"
                >
                  <Maximize2 className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                </button>
                {/* Per-card "new" pill — shown if the map's createdAt is
                    newer than the snapshot of lastSeen taken when the
                    user opened this view. */}
                {isNew && (
                  <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                    NEW
                  </span>
                )}
                <div className="p-2 text-[11px] text-gray-600 space-y-0.5">
                  <div className="font-medium text-gray-800 truncate">
                    {m.activity_name || 'Activity'}
                  </div>
                  <div className="text-gray-400">
                    {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {enlarged && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2"
          onClick={() => setEnlarged(null)}
        >
          <div
            className="bg-white p-3 rounded-2xl relative w-[98vw] max-w-[1100px] max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-2 flex-shrink-0">
              <h3 className="text-base font-semibold truncate">
                {enlarged.activity_name || 'Activity map'}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setEnlarged(null)}
                className="text-gray-500 hover:text-gray"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <img
              src={enlarged.imageUrl}
              alt={enlarged.activity_name || 'Activity map'}
              className="flex-1 min-h-0 w-full object-contain shadow-2xl shadow-black/50"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookMapsView;
